import { useState, useEffect, useRef, useCallback } from "react";

/** Maximum age for persisted state: 4 hours (in ms). */
const DEFAULT_TTL = 4 * 60 * 60 * 1000;
const liveSessionState = new Map<string, unknown>();

/**
 * Persists state across route changes, tab switches, and full reloads.
 * Entries automatically expire after the TTL (default 4 hours).
 */
export function useSessionState<T>(
  key: string,
  defaultValue: T,
  ttl: number = DEFAULT_TTL
): [T, (value: T | ((prev: T) => T)) => void] {
  const readStoredValue = (): T | null => {
    try {
      const now = Date.now();

      const parseEntry = (raw: string | null) => {
        if (raw === null) return null;

        const parsed = JSON.parse(raw);
        if (
          parsed &&
          typeof parsed === "object" &&
          "v" in parsed &&
          "t" in parsed &&
          typeof parsed.t === "number"
        ) {
          // Expired → treat as missing
          if (now - parsed.t > ttl) return null;
          return { value: parsed.v as T, timestamp: parsed.t as number };
        }

        // Backward compat: plain values have no timestamp → treat as expired
        return null;
      };

      const sessionEntry = parseEntry(sessionStorage.getItem(key));
      const localEntry = parseEntry(localStorage.getItem(key));

      // If both expired, clean up storage
      if (!sessionEntry && !localEntry) {
        try { sessionStorage.removeItem(key); } catch {}
        try { localStorage.removeItem(key); } catch {}
        return null;
      }

      if (!localEntry) return sessionEntry!.value;
      if (!sessionEntry) return localEntry.value;

      return sessionEntry.timestamp >= localEntry.timestamp
        ? sessionEntry.value
        : localEntry.value;
    } catch {
      return null;
    }
  };

  const [state, setState] = useState<T>(() => {
    if (liveSessionState.has(key)) return liveSessionState.get(key) as T;
    const storedValue = readStoredValue();
    const initialValue = storedValue ?? defaultValue;
    liveSessionState.set(key, initialValue);
    return initialValue;
  });

  const setSessionState = useCallback((value: T | ((prev: T) => T)) => {
    setState((previous) => {
      const next = typeof value === "function"
        ? (value as (prev: T) => T)(previous)
        : value;
      liveSessionState.set(key, next);
      return next;
    });
  }, [key]);

  // Skip the first effect run so we don't refresh the TTL timestamp
  // just because the component mounted. Only real user-driven state
  // changes should extend the persistence window.
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    let idleId: number | null = null;
    const timer = window.setTimeout(() => {
      const persist = () => {
        try {
          const serialized = JSON.stringify({ v: state, t: Date.now() });
          try { sessionStorage.setItem(key, serialized); } catch {}
          try { localStorage.setItem(key, serialized); } catch {}
        } catch {
          // Storage is a convenience; never interrupt the interaction.
        }
      };

      if ("requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(persist, { timeout: 1200 });
      } else {
        persist();
      }
    }, 350);

    return () => {
      window.clearTimeout(timer);
      if (idleId !== null && "cancelIdleCallback" in window) window.cancelIdleCallback(idleId);
    };
  }, [key, state]);

  return [state, setSessionState];
}

/**
 * Call once on app boot to purge all expired useSessionState entries.
 * Scans localStorage for entries matching the { v, t } shape and removes stale ones.
 */
export function purgeExpiredSessionState(ttl: number = DEFAULT_TTL): void {
  const now = Date.now();
  const keysToRemove: string[] = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        if (
          parsed &&
          typeof parsed === "object" &&
          "v" in parsed &&
          "t" in parsed &&
          typeof parsed.t === "number" &&
          now - parsed.t > ttl
        ) {
          keysToRemove.push(key);
        }
      } catch {
        // Not JSON or not our format — skip
      }
    }

    for (const key of keysToRemove) {
      localStorage.removeItem(key);
      try { sessionStorage.removeItem(key); } catch {}
    }
  } catch {
    // Storage access error — ignore
  }
}
